import { Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import {
  ApiUrlInjectionToken,
  CustomUnit,
  PerformanceAccessoryAnswer,
  UnitPerformanceDataResponse,
} from '@mitsubishi/shared/data-access';
import { getRequestTraceHttpHeaders } from '@mitsubishi/shared/request-tracing';

import { FormlyFieldConfig } from '@ngx-formly/core';

import { ExternalUnit, SimulationRequest, SimulationResponse } from '../models';

@Injectable({
  providedIn: 'root',
})
export class UnitService {
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    @Inject(ApiUrlInjectionToken) apiServerUrl: string
  ) {
    this.baseUrl = apiServerUrl + '/project';
  }

  softDeleteUnit(projectId: string, selectionId: string, unitId: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/softDelete`, {});
  }

  restoreUnit(projectId: string, selectionId: string, unitId: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/restore`, {});
  }

  deleteUnit(projectId: string, selectionId: string, unitId: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}`);
  }

  softDeleteUnitCustom(projectId: string, selectionId: string, unitId: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitId}/soft`);
  }

  restoreUnitCustom(projectId: string, selectionId: string, unitId: string): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitId}/restore`,
      {}
    );
  }

  deleteUnitCustom(projectId: string, selectionId: string, unitId: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitId}`);
  }

  createUnitFromTemplate(
    projectId: string,
    selectionId: string,
    alternativeId: string,
    entityId: string,
    entityType: string
  ): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit-from-template`, {
      alternativeId,
      entityId,
      entityType,
    });
  }

  createUnitFromBleen(
    projectId: string,
    selectionId: string,
    alternativeId: string,
    bleenId: string
  ): Observable<{ uuid: string }> {
    return this.http.post<{ uuid: string }>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit`, {
      alternativeId,
      bleenId,
    });
  }

  duplicateUnit(projectId: string, selectionId: string, unitId: string): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/duplicate`, {});
  }

  linkUnitAlternative(projectId: string, selectionId: string, unitId: string, alternativeId: string): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/linkAlternative`,
      {
        alternativeId,
      }
    );
  }

  unlinkUnitAlternative(
    projectId: string,
    selectionId: string,
    unitId: string,
    alternativeId: string
  ): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/unlinkAlternative`,
      {
        alternativeId,
      }
    );
  }

  linkUnitCustomAlternative(
    projectId: string,
    selectionId: string,
    unitId: string,
    alternativeId: string
  ): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitId}/link-alternative`,
      {
        alternativeId,
      }
    );
  }

  unlinkUnitCustomAlternative(
    projectId: string,
    selectionId: string,
    unitId: string,
    alternativeId: string
  ): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitId}/unlink-alternative`,
      {
        alternativeId,
      }
    );
  }

  moveUnit(projectId: string, selectionId: string, unitId: string, request: { position: number }): Observable<null> {
    return this.http.post<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/move`, request);
  }

  moveUnitCustom(
    projectId: string,
    selectionId: string,
    unitId: string,
    request: { position: number }
  ): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitId}/move`,
      request
    );
  }

  updateQuantity(
    projectId: string,
    selectionId: string,
    unitId: string,
    alternativeId: string,
    quantity: number
  ): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/quantity`, {
      alternativeId,
      quantity,
    });
  }

  updateQuantityCustom(
    projectId: string,
    selectionId: string,
    unitId: string,
    alternativeId: string,
    quantity: number
  ): Observable<null> {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitId}/quantity`,
      {
        alternativeId,
        quantity,
      }
    );
  }

  getUnitPerformance(projectId: string, unitId: string | undefined): Observable<UnitPerformanceDataResponse> {
    return this.http.get<UnitPerformanceDataResponse>(
      `${this.baseUrl}/${projectId}/selection/unit/${unitId}/performance`
    );
  }

  selectUnitPerformance({
    projectId,
    selectionId,
    unitId,
    performanceId,
  }: {
    projectId: string;
    selectionId: string;
    unitId: string;
    performanceId: string;
  }): Observable<UnitPerformanceDataResponse> {
    return this.http.put<UnitPerformanceDataResponse>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/performance/${performanceId}`,
      undefined
    );
  }

  getUnitWorkingConditions(projectId: string, unitId: string | undefined): Observable<FormlyFieldConfig[]> {
    return this.http.get<FormlyFieldConfig[]>(
      `${this.baseUrl}/${projectId}/selection/unit/${unitId}/working-conditions`
    );
  }

  updateWorkingConditions(
    projectId: string,
    selectionId: string,
    unitId: string,
    data: any,
    mergeConditions: boolean
  ): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/working-conditions`,
      {
        data,
        mergeConditions,
      }
    );
  }

  simulate(
    projectId: string,
    unitId: string,
    simulation: SimulationRequest,
    requestTraceId?: string
  ): Observable<SimulationResponse> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);
    return this.http.post<SimulationResponse>(
      `${this.baseUrl}/${projectId}/selection/unit/${unitId}/simulation`,
      simulation,
      { headers }
    );
  }

  duplicateSession(projectId: string, selectionId: string, unitId: string): Observable<null> {
    return this.http.put<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}`, {});
  }

  updateWarranty(
    projectId: string,
    selectionId: string,
    unitId: string,
    alternativeId: string,
    warrantyCode: string
  ): Observable<null> {
    const body = {
      warrantyCode: warrantyCode,
      alternativeId: alternativeId,
    };
    return this.http.patch<null>(`${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/warranty`, body);
  }

  createCustomUnit(projectId: string, selectionId: string, unitCustom: CustomUnit) {
    return this.http.post<{ uuid: string }>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom`,
      unitCustom
    );
  }

  editCustomUnit(
    projectId: string,
    selectionId: string,
    unitCustomId: string,
    unitCustom: CustomUnit
  ): Observable<null> {
    return this.http.put<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitCustomId}`,
      unitCustom
    );
  }

  assignAccessories(
    projectId: string,
    selectionId: string,
    unitId: string,
    answers: PerformanceAccessoryAnswer[]
  ): Observable<null> {
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/assign-accessories`,
      answers
    );
  }

  updateUnitCustomWarranty(
    projectId: string,
    selectionId: string,
    unitId: string,
    alternativeId: string,
    warrantyCode: string
  ): Observable<null> {
    const body = {
      warrantyCode: warrantyCode,
      alternativeId: alternativeId,
    };
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitId}/warranty`,
      body
    );
  }

  hasExternalUnitPresence(projectId: string, unitId: string) {
    return this.http.get<{ hasExternalUnits: boolean }>(
      `${this.baseUrl}/${projectId}/unit/${unitId}/external-units/presence`
    );
  }

  hasExternalUnitPresenceFromStudy(projectId: string, studyId: string, unitId: string) {
    return this.http.get<{ hasExternalUnits: boolean }>(
      `${this.baseUrl}/${projectId}/study/${studyId}/unit/${unitId}/external-units/presence`
    );
  }

  getExternalUnits(projectId: string, unitId: string, workingConditions: any): Observable<ExternalUnit[]> {
    return this.http.post<ExternalUnit[]>(
      `${this.baseUrl}/${projectId}/selection/unit/${unitId}/external-units`,
      workingConditions
    );
  }

  getExternalUnitsWorkingConditions(projectId: string, unitId: string): Observable<FormlyFieldConfig[]> {
    return this.http.get<FormlyFieldConfig[]>(
      `${this.baseUrl}/${projectId}/selection/unit/${unitId}/external-units/working-conditions`
    );
  }

  addExternalUnit(
    projectId: string,
    selectionId: string,
    unitId: string,
    bleenId: string,
    quantity: number,
    workingCondition: any,
    requestTraceId?: string
  ): Observable<null> {
    const headers = getRequestTraceHttpHeaders(requestTraceId);
    return this.http.post<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/external-unit`,
      {
        bleenId,
        quantity,
        workingCondition,
      },
      { headers }
    );
  }

  updateLoadMode(unitId: string, loadMode: number): Observable<null> {
    return this.http.patch<null>(`${this.baseUrl}/selection/unit/${unitId}/load-mode`, { loadMode });
  }

  updateDescription(projectId: string, selectionId: string, unitId: string, description?: string) {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit/${unitId}/update-description`,
      { description }
    );
  }

  updateUnitCustomDescription(projectId: string, selectionId: string, unitCustomId: string, description?: string) {
    return this.http.patch<null>(
      `${this.baseUrl}/${projectId}/selection/${selectionId}/unit-custom/${unitCustomId}/description`,
      { description }
    );
  }
}
